import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, Undo2, Redo2, Unlink, Heading2, Heading3 } from "lucide-react";

const Btn = ({ onClick, active, title, testId, children, disabled }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`h-9 w-9 inline-flex items-center justify-center rounded-lg transition-colors ${active ? "bg-deep-rose text-white" : "text-stone-600 hover:bg-stone-100"} disabled:opacity-40`}
  >
    {children}
  </button>
);

export const RichTextEditor = ({ value, onChange, placeholder, testId = "rich-editor", hideVariables = false, showHeadings = false }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, codeBlock: false, horizontalRule: false }),
      Underline,
      Highlight,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { style: "color:#A8556B;" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class: "prose-email min-h-[220px] px-3 py-3 focus:outline-none",
        "data-testid": `${testId}-content`,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next !== current && !(next === "" && current === "<p></p>")) {
      editor.commands.setContent(next, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Adresse du lien (https://…)", prev);
    if (url === null) return;
    if (url.trim() === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  const insertVar = (name) => editor.chain().focus().insertContent(`{${name}}`).run();

  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden bg-white" data-testid={testId}>
      <div className="flex items-center gap-0.5 flex-wrap border-b border-stone-200 bg-stone-50 px-1.5 py-1">
        {showHeadings && (
          <>
            <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Titre" testId={`${testId}-h2`}><Heading2 size={16} /></Btn>
            <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Sous-titre" testId={`${testId}-h3`}><Heading3 size={16} /></Btn>
            <span className="w-px h-6 bg-stone-200 mx-1" />
          </>
        )}
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Gras" testId={`${testId}-bold`}><Bold size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italique" testId={`${testId}-italic`}><Italic size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Souligné" testId={`${testId}-underline`}><UnderlineIcon size={16} /></Btn>
        <span className="w-px h-6 bg-stone-200 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Liste à puces" testId={`${testId}-bullet`}><List size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Liste numérotée" testId={`${testId}-ordered`}><ListOrdered size={16} /></Btn>
        <span className="w-px h-6 bg-stone-200 mx-1" />
        <Btn onClick={setLink} active={editor.isActive("link")} title="Insérer un lien" testId={`${testId}-link`}><Link2 size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive("link")} title="Retirer le lien" testId={`${testId}-unlink`}><Unlink size={16} /></Btn>
        <span className="w-px h-6 bg-stone-200 mx-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler" testId={`${testId}-undo`}><Undo2 size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rétablir" testId={`${testId}-redo`}><Redo2 size={16} /></Btn>
      </div>

      <div className={`items-center gap-1.5 flex-wrap border-b border-stone-100 bg-white px-2 py-1.5 ${hideVariables ? "hidden" : "flex"}`}>
        <span className="text-[11px] uppercase tracking-widest text-stone-400 mr-1">Variables</span>
        {[
          ["prenom_parent", "Prénom parent"],
          ["nom_parent", "Nom parent"],
          ["eleves", "Élève(s)"],
          ["cours", "Cours"],
        ].map(([k, label]) => (
          <button key={k} type="button" onClick={() => insertVar(k)} data-testid={`${testId}-var-${k}`} className="text-xs px-2 py-1 rounded-full bg-blush-light text-deep-rose hover:bg-blush transition-colors">
            {label}
          </button>
        ))}
      </div>

      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
